import { html, nothing, svg, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { createActorState, type ActorStateOf } from "../state/actor-state";
import { ZipperEmbedElement } from "./base/zipper-embed-element";
import { shopStyles } from "./shop/shop.styles";
import type { ComponentType, EmbedConfig, ShopOptions } from "../config/types";
import {
  GIFT_MAX_CENTS,
  GIFT_MIN_CENTS,
  MOCK_GIFT_CARDS,
  MOCK_MEMBERSHIPS,
  MOCK_PACKS,
  MOCK_SPECIALS,
  isFixedDenominationGift,
  isGiftCardProduct,
  type MockProduct,
} from "./data/mock-shop";
import { formatShortAmount } from "./format-money";
import {
  fetchProducts,
  fetchMemberContext,
  type ShopProducts,
} from "../data/live";
import "./internal/zipper-purchase-checkout";
import "./internal/zipper-commerce-checkout";
import { clearEmbedToken } from "../config/client";
import {
  CommerceOfferingKind,
  CommerceRequestedAction,
  type CommerceGiftOfferingRefDto,
  type CommerceProductOfferingRefDto,
} from "@zipper/customer-commerce-client";

// The fixed type-based sections, used only as the fallback when the provider
// hasn't arranged products into named subcategories via Manage Shop View. All
// non-empty sections render in this order — there is no per-embed type filter.
type SectionKey = "specials" | "memberships" | "packs" | "giftCards";

const SECTION_ORDER: SectionKey[] = [
  "specials",
  "memberships",
  "packs",
  "giftCards",
];

const SECTION_TITLES: Record<SectionKey, string> = {
  specials: "New Client Specials",
  memberships: "Memberships",
  packs: "Class Packs",
  giftCards: "Gift Cards",
};

// Subtitles mirror the handoff `pricing.jsx` section copy.
const SECTION_SUBTITLES: Record<SectionKey, string> = {
  specials: "Welcome offers for your first visit.",
  memberships: "Recurring monthly access — cancel anytime.",
  packs: "Punch cards you can use at your own pace.",
  giftCards: "Give the gift of movement.",
};

// Default callout banner colors from `PRICING_TWEAK_DEFAULTS` (clay, not accent).
const DEFAULT_CALLOUT_BG = "#C77B5F";
const DEFAULT_CALLOUT_TEXT = "#FFFFFF";

const MOCK_PRODUCTS: ShopProducts = {
  specials: MOCK_SPECIALS,
  memberships: MOCK_MEMBERSHIPS,
  packs: MOCK_PACKS,
  giftCards: MOCK_GIFT_CARDS,
  sections: [],
};

const shopActorState = createActorState(() => ({
  products: {
    specials: [],
    memberships: [],
    packs: [],
    giftCards: [],
    sections: [],
  } as ShopProducts,
  buying: null as MockProduct | null,
  signedInName: "",
  hasPriorPurchases: false,
}));

type ShopActorState = ActorStateOf<typeof shopActorState>;

/**
 * <zipper-shop instance="..."> — product catalog (memberships, packs, gift
 * cards). Ported from the handoff `pricing.jsx`. Mock data for now.
 */
@customElement("zipper-shop")
export class ZipperShop extends ZipperEmbedElement {
  static styles = shopStyles;

  @shopActorState.field("products")
  private products!: ShopActorState["products"];

  @shopActorState.field("buying")
  private buying!: ShopActorState["buying"];

  @shopActorState.field("signedInName")
  private signedInName!: ShopActorState["signedInName"];

  @shopActorState.field("hasPriorPurchases")
  private hasPriorPurchases!: ShopActorState["hasPriorPurchases"];
  private loadedAuthorityKey = "";
  private initialProductOpened = false;

  protected get expectedType(): ComponentType {
    return "shop";
  }

  private get opts(): ShopOptions {
    return (this.config?.options as ShopOptions) ?? {};
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    if ((changed.has("config") || changed.has("runtime")) && this.config) {
      const authorityKey = this.discoveryAuthorityKey();
      if (authorityKey === this.loadedAuthorityKey) return;
      this.loadedAuthorityKey = authorityKey;
      this.products = {
        specials: [],
        memberships: [],
        packs: [],
        giftCards: [],
        sections: [],
      };
      this.buying = null;
      this.initialProductOpened = false;
      this.signedInName = "";
      this.hasPriorPurchases = false;
      void this.loadProducts(authorityKey);
      void this.loadMember(authorityKey);
    }
  }

  private discoveryAuthorityKey(): string {
    return JSON.stringify({
      orgId: this.config?.orgId ?? 0,
      options: this.config?.options ?? {},
      giftCardsEnabled: this.config?.giftCardsEnabled,
      viewerRevision: this.runtime.viewerRevision ?? "guest",
    });
  }

  private async loadMember(authorityKey: string): Promise<void> {
    if (!this.config?.orgId) return;
    const ctx = await fetchMemberContext(this.config.orgId);
    if (authorityKey !== this.loadedAuthorityKey) return;
    if (!ctx) {
      this.signedInName = "";
      this.hasPriorPurchases = false;
      return;
    }
    this.signedInName = ctx.firstName?.trim() || "there";
    this.hasPriorPurchases = !!ctx.hasPriorPurchases;
  }

  private logout(): void {
    if (this.config?.orgId) clearEmbedToken(this.config.orgId);
    this.signedInName = "";
    this.hasPriorPurchases = false;
  }

  private productVisible(item: MockProduct): boolean {
    if (this.runtime.surface === "dashboard-customer" && this.runtime.commerce && item.deferredChargeSettings?.enabled) return false;
    if (!item.firstTimeClientsOnly) return true;
    return !this.hasPriorPurchases;
  }

  /**
   * Whether this org sells gift cards, per the public embed config.
   *
   * OPT-OUT. Only an explicit `giftCardsEnabled === false` disables; absent or
   * undefined means ENABLED. That is deliberate and load-bearing: the config
   * endpoint is edge-cached, so a payload minted before the field existed omits
   * it, and every org that never opened the gift-card settings screen has no
   * `giftCardSettings` block at all. It also mirrors the server guard exactly —
   * `gift-card.service.ts` rejects a purchase only when
   * `giftCardSettings?.enableGiftCards === false`. Writing this as `=== true`
   * or `Boolean(...)` is the `issuePunchCard` inversion.
   */
  private get giftCardsEnabled(): boolean {
    return this.config?.giftCardsEnabled !== false;
  }

  /**
   * The products this shop is allowed to render.
   *
   * When the org has gift cards switched off the card must not appear AT ALL —
   * not merely with a disabled button. `/gift-cards/purchase-intent` 400s for
   * these, so a rendered card is a dead end that only reveals itself after the
   * buyer has picked an amount and typed a recipient.
   *
   * Applied to BOTH surfaces a gift card can reach the shop through: the
   * `giftCards` type-section AND a provider "Manage Shop View" subcategory in
   * `sections`, which bypasses the type buckets entirely. `isGiftCardProduct`
   * reads the product's own `category`/`cadence`, which `data/live.ts` carries
   * onto every product precisely so the subcategory path stays detectable.
   */
  private sellable(items: MockProduct[]): MockProduct[] {
    if (this.giftCardsEnabled) return items;
    return items.filter((i) => !isGiftCardProduct(i));
  }

  /** Load the org's real products; mock only when no org is configured. */
  private async loadProducts(authorityKey: string): Promise<void> {
    if (!this.config) return;
    if (!this.config.orgId) {
      if (authorityKey !== this.loadedAuthorityKey) return;
      this.products = MOCK_PRODUCTS;
      this.openInitialProduct(MOCK_PRODUCTS);
      return;
    }
    try {
      const live = await fetchProducts(this.config.orgId);
      if (authorityKey !== this.loadedAuthorityKey) return;
      if (
        live.memberships.length ||
        live.packs.length ||
        live.giftCards.length ||
        live.sections.length
      ) {
        this.products = live;
      }
      this.openInitialProduct(live);
    } catch {
      /* leave empty for live org */
    }
  }

  private openInitialProduct(products: ShopProducts): void {
    if (this.initialProductOpened || !this.opts.initialProductId) return;
    const productId = this.opts.initialProductId;
    const product = this.sellable([
      ...products.specials,
      ...products.memberships,
      ...products.packs,
      ...products.giftCards,
      ...products.sections.flatMap((section) => section.items),
    ]).find(
      (candidate) =>
        candidate.id === productId || candidate.stripeProductId === productId,
    );
    if (!product || !this.productVisible(product)) return;
    this.initialProductOpened = true;
    this.buying = product;
  }

  protected renderContent(_config: EmbedConfig): TemplateResult {
    if (this.buying) {
      const p = this.buying;
      const o = this.opts;
      if (
        this.runtime.surface === "dashboard-customer" &&
        this.runtime.commerce &&
        p.stripeProductId
      ) {
        const gift = isGiftCardProduct(p);
        const offering:
          CommerceGiftOfferingRefDto | CommerceProductOfferingRefDto = gift
          ? {
              kind: CommerceOfferingKind.Gift,
              offeringKey: `gift:${p.stripeProductId}`,
              giftProductKey: p.stripeProductId,
            }
          : {
              kind: CommerceOfferingKind.Product,
              offeringKey: `product:${p.stripeProductId}`,
              productKey: p.stripeProductId,
            };
        return html`<zipper-commerce-checkout
          .runtime=${this.runtime}
          .offering=${offering}
          .requestedAction=${CommerceRequestedAction.Purchase}
          .orgId=${this.config?.orgId ?? 0}
          .title=${p.name}
          .subtitle=${p.description}
          .stripe=${this.config?.stripe}
          .allowCustomAmount=${gift && !isFixedDenominationGift(p)}
          @zipper-commerce-success=${() =>
            this.dispatchEvent(
              new CustomEvent("zipper-purchase", {
                detail: {
                  productId: p.stripeProductId,
                  name: p.name,
                },
                bubbles: true,
                composed: true,
              }),
            )}
        ></zipper-commerce-checkout>`;
      }
      return html`<zipper-purchase-checkout
        .deferredChargeSettings=${p.deferredChargeSettings}
        .productName=${p.name}
        .productSub=${p.description}
        .price=${p.price}
        .cadence=${p.cadence}
        .features=${p.features ?? []}
        .stripeProductId=${p.stripeProductId ?? ""}
        .formId=${p.formId ?? 0}
        .stripe=${this.config?.stripe}
        .orgId=${this.config?.orgId ?? 0}
        .accent=${this.config?.theme?.accent ?? "#4F9DB5"}
        .orgSlug=${this.config?.orgSlug ?? ""}
        .hideTerms=${!!o.hideTerms}
        .requirePhone=${!!o.requirePhone}
        .requiredSignupFields=${this.config?.requiredSignupFields}
        .currency=${this.config?.currency ?? ""}
        .hideRecipientBooking=${!!o.hideRecipientBooking}
        .firstTimeClientsOnly=${!!p.firstTimeClientsOnly}
        .isGift=${isGiftCardProduct(p)}
        @zipper-purchase-close=${() => (this.buying = null)}
      ></zipper-purchase-checkout>`;
    }
    const o = this.opts;
    // SHOP-ORD: when the provider has arranged products into subcategories via
    // Manage Shop View, the embed follows those groups + order. Otherwise it
    // falls back to the fixed type-based sections, showing ALL product types
    // (item 12 removed the per-embed "Show product types" filter — the embed
    // reflects Manage Shop View, not a per-embed toggle).
    const subSections = this.products.sections;
    const visible: {
      title: string;
      subtitle?: string;
      items: MockProduct[];
    }[] =
      subSections.length > 0
        ? // Filter AFTER `sellable()`, not before: a subcategory whose only
          // member is a gift card must disappear along with the card, rather
          // than render as an empty titled section.
          subSections
            .map((s) => ({ title: s.title, items: this.sellable(s.items) }))
            .filter((s) => s.items.length > 0)
        : SECTION_ORDER.map((key) => ({
            title: SECTION_TITLES[key],
            subtitle: SECTION_SUBTITLES[key],
            // SHOP-41: render each category's products in the order the
            // adapter provides — the API pre-sorts by ordinal (asc, nulls
            // last) and `data/live.ts` preserves that server order.
            items: this.sellable(this.products[key]),
          })).filter((s) => s.items.length > 0);
    // SHOP-42: expose the provider's callout colors as tokens so every banner
    // strip reads them from CSS (bg = callout color, label = callout-text color).
    const calloutVars =
      `--zui-callout-bg:${o.callout ?? DEFAULT_CALLOUT_BG};` +
      `--zui-callout-text:${o.calloutText ?? DEFAULT_CALLOUT_TEXT}`;
    return html`
      <div class="wrap" style=${calloutVars}>
        ${this.renderShopSignIn()}
        ${visible.length === 0
          ? html`<div class="zui-state">No products available.</div>`
          : visible.map((section) => {
              const items = section.items.filter((i) => this.productVisible(i));
              if (!items.length) return nothing;
              // When any card in the section has a callout banner, reserve the
              // banner space on the others so card bodies stay aligned.
              const anyCallout =
                !o.hideCallouts && section.items.some((i) => i.callout);
              return html`
                <section class="section">
                  <h2 class="section-title">${section.title}</h2>
                  ${section.subtitle
                    ? html`<div class="section-subtitle">
                        ${section.subtitle}
                      </div>`
                    : nothing}
                  <div class="grid">
                    ${items.map((item) => this.renderCard(item, anyCallout))}
                  </div>
                </section>
              `;
            })}
      </div>
    `;
  }

  // F2: the "Already a customer? Sign in" prompt is intentionally NOT rendered
  // on this landing screen — it's redundant with the equivalent prompt on the
  // purchase checkout screen (zipper-purchase-checkout.ts). A visitor who is
  // already signed in (e.g. from a prior checkout in this session) still sees
  // a welcome banner here.
  private renderShopSignIn(): TemplateResult | typeof nothing {
    if (!this.signedInName) return nothing;
    return html`<div class="section-subtitle" style="margin-bottom:0.5rem">
      Welcome back, ${this.signedInName}.
      <button
        type="button"
        class="zui-cta"
        style="display:inline-flex;padding:0.25rem 0.6rem;margin-left:0.5rem"
        @click=${() => this.logout()}
      >
        Log out
      </button>
    </div>`;
  }

  private renderCard(
    item: MockProduct,
    reserveCallout: boolean,
  ): TemplateResult {
    const o = this.opts;
    const showCallout = !!item.callout && !o.hideCallouts;
    // A custom-amount gift card's catalog `price` is a placeholder the buyer
    // replaces at checkout (real data: "Enter your price!" at $1, "Big
    // Donation" at $0). Printing it as the price would be a lie, and its
    // cadence label is the literal string "custom" — so show the prompt
    // instead. A fixed-denomination gift card ($50) prices normally.
    const askAmount = isGiftCardProduct(item) && !isFixedDenominationGift(item);
    const currency = this.config?.currency;
    const arrow = svg`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>`;
    return html`
      <div class="card">
        ${showCallout
          ? html`<div class="callout">${item.callout}</div>`
          : reserveCallout
            ? html`<div class="callout placeholder">·</div>`
            : nothing}
        <div class="card-body">
          <div class="pname">${item.name}</div>
          ${item.deferredChargeSettings?.enabled ? html`<div class="cad">Pay later · ${item.deferredChargeSettings.triggerType === "fixed_date" ? `Starts ${item.deferredChargeSettings.fixedChargeDate}` : "Charged on your first booking"}</div>` : nothing}
          <div class="price">
            ${askAmount
              ? html`<span class="amt gift-any">You choose</span>
                  <span class="cad"
                    >${formatShortAmount(GIFT_MIN_CENTS / 100, currency)} –
                    ${formatShortAmount(GIFT_MAX_CENTS / 100, currency)}</span
                  >`
              : html`<span class="amt"
                    >${formatShortAmount(item.price, currency)}</span
                  >
                  <span class="cad">${item.cadence}</span>`}
          </div>
          ${o.hideDescription
            ? html`<div class="desc spacer"></div>`
            : html`<div class="desc">${item.description}</div>`}
          ${o.hideButton
            ? nothing
            : html`<button class="zui-cta" @click=${() => this.onBuy(item)}>
                ${o.buttonText ?? "Buy now"}${arrow}
              </button>`}
        </div>
      </div>
    `;
  }

  /** Open the inline 2-step purchase checkout for the chosen product. */
  private onBuy(item: MockProduct): void {
    this.dispatchEvent(
      new CustomEvent("zipper-purchase", {
        detail: { productId: item.id, name: item.name },
        bubbles: true,
        composed: true,
      }),
    );
    this.buying = item;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-shop": ZipperShop;
  }
}
